import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { DEFAULT_LANGUAGE, LANGUAGES, Language } from '../types/language';
const STORAGE_KEY = 'mvc-language';
type LanguageContextValue = {
  language: Language;
  setLanguage: (language: Language) => void;
};
const LanguageContext = createContext<LanguageContextValue | undefined>(undefined);
function readStoredLanguage(): Language {
  if (typeof window === 'undefined') return DEFAULT_LANGUAGE;
  try {
    const stored = window.localStorage.getItem(STORAGE_KEY);
    if (stored === 'nl' || stored === 'en') return stored;
  } catch {
    // localStorage unavailable (private mode, blocked cookies) — fall back
  }
  return DEFAULT_LANGUAGE;
}
export function LanguageProvider({
  children
}: {
  children: React.ReactNode;
}) {
  const [language, setLanguageState] = useState<Language>(readStoredLanguage);
  useEffect(() => {
    try {
      window.localStorage.setItem(STORAGE_KEY, language);
    } catch {
      // Ignore write failures — the language still applies for this visit
    }
    const match = LANGUAGES.find(entry => entry.code === language);
    if (match) {
      document.documentElement.lang = match.htmlLang;
    }
  }, [language]);
  const setLanguage = useCallback((next: Language) => {
    setLanguageState(next);
  }, []);
  const value = useMemo(() => ({
    language,
    setLanguage
  }), [language, setLanguage]);
  return <LanguageContext.Provider value={value} data-id="element-257" data-mp="2z02kkj7krmyu">
      {children}
    </LanguageContext.Provider>;
}
export function useLanguage(): LanguageContextValue {
  const context = useContext(LanguageContext);
  if (!context) {
    throw new Error('useLanguage must be used within a LanguageProvider');
  }
  return context;
}